import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, TextInput, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { loadAuditLog } from '../db/company';
import { selectedJobAtom, selectedVersionAtom } from '../store/company';
import moment from 'moment';
import xlsx from 'xlsx';

const ACTION_OPTIONS = ['', 'INSERT', 'UPDATE', 'DELETE', 'EXPORT'];
const TAB_OPTIONS = [
    '', 'Forecast', 'Burn Tool Labor', 'Burn Tool Equipment Phased', 'Materials', 'Rentals',
    'Subcontractors', 'Other', 'All Third Party', 'Contingency', 'CO Log', 'Day Zero Budget'
];

const PAGE_LIMIT = 200;

const AuditLog = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);

    // Filter state. Defaults: scope to current job + version, no other filters.
    const [filterJob, setFilterJob] = useState(selectedJob || '');
    const [filterVersion, setFilterVersion] = useState(selectedVersion || '');
    const [filterTab, setFilterTab] = useState('');
    const [filterEmail, setFilterEmail] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterRowId, setFilterRowId] = useState('');
    const [filterStart, setFilterStart] = useState('');
    const [filterEnd, setFilterEnd] = useState('');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const buildQuery = useCallback((beforeId = null) => ({
        buId: filterJob || null,
        versionGuid: filterVersion || null,
        tab: filterTab || null,
        userEmail: filterEmail || null,
        actionType: filterAction || null,
        rowId: filterRowId || null,
        startDate: filterStart || null,
        endDate: filterEnd || null,
        beforeId,
        limit: PAGE_LIMIT,
    }), [filterJob, filterVersion, filterTab, filterEmail, filterAction, filterRowId, filterStart, filterEnd]);

    // Stash the latest fetcher in a ref so the filter-change effect can call it
    // without listing it as a dep (which would loop, since fetchPage closes over
    // `rows` for keyset pagination).
    const fetchPageRef = useRef();
    fetchPageRef.current = async (append = false) => {
        setLoading(true);
        setError(null);
        try {
            const beforeId = append && rows.length > 0 ? rows[rows.length - 1].Audit_ID : null;
            const res = await loadAuditLog(buildQuery(beforeId));
            const next = res?.data || [];
            setRows(prev => append ? [...prev, ...next] : next);
            setHasMore(next.length === PAGE_LIMIT);
        } catch (e) {
            setError(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    };
    const fetchPage = useCallback((append = false) => fetchPageRef.current && fetchPageRef.current(append), []);

    // Initial load when job changes (and on mount).
    useEffect(() => {
        setFilterJob(selectedJob || '');
        setFilterVersion(selectedVersion || '');
    }, [selectedJob, selectedVersion]);

    useEffect(() => {
        // Auto-load when filters change. Replace, don't append.
        fetchPage(false);
    }, [filterJob, filterVersion, filterTab, filterEmail, filterAction, filterRowId, filterStart, filterEnd, fetchPage]);

    const actionColor = (a) => a === 'INSERT' ? '#059669' : a === 'UPDATE' ? '#2563eb' : a === 'DELETE' ? '#dc2626' : a === 'EXPORT' ? '#7c3aed' : '#64748b';
    const truncate = (s, n = 40) => {
        if (s == null) return '';
        const str = String(s);
        return str.length > n ? str.slice(0, n - 1) + '…' : str;
    };
    // Audit_Datetime is written by GETDATE() on the SQL server, which runs in
    // US Central — so the stored value is already Central wall-clock, NOT UTC.
    // The mssql driver hands it back mislabeled with a trailing 'Z'; moment.utc
    // reads those exact components, and we DO NOT call .local() — doing so would
    // subtract the Central offset a second time and show every change ~5h early
    // (a 9am edit rendered as 4am). Label it CT to make the zone explicit.
    const fmtDate = (d) => d ? moment.utc(d).format('YYYY-MM-DD HH:mm:ss') + ' CT' : '';

    const stats = useMemo(() => {
        const c = { INSERT: 0, UPDATE: 0, DELETE: 0 };
        rows.forEach(r => { if (c[r.Action_Type] != null) c[r.Action_Type]++; });
        return c;
    }, [rows]);

    // Export the currently-loaded rows to an .xlsx workbook. Mirrors the visible
    // columns plus the full prev/new row JSON so the export is self-contained.
    const exportExcel = () => {
        if (!rows.length) return;
        const sheetRows = rows.map(r => ({
            'When': fmtDate(r.Audit_Datetime),
            'Action': r.Action_Type || '',
            'User': r.User_Email || r.User_Name || r.User_ID || '',
            'Job': r.BU_ID || '',
            'Tab': r.Forecast_Tab || '',
            'Row ID': r.Forecast_Row_ID || '',
            'Field': r.Forecast_Field_Name || '',
            'Prev Value': r.Prev_Value ?? '',
            'New Value': r.New_Value ?? '',
            'Env': r.Env || '',
            'Source': r.Source || '',
            'Version GUID': r.Version_GUID || '',
            'FUD GUID': r.Forecast_User_Data_GUID || '',
            'Audit ID': r.Audit_ID,
            'Prev Row JSON': r.Prev_Row_JSON || '',
            'New Row JSON': r.New_Row_JSON || '',
        }));
        const ws = xlsx.utils.json_to_sheet(sheetRows);
        ws['!cols'] = [
            { wpx: 160 }, // When
            { wpx: 70 },  // Action
            { wpx: 200 }, // User
            { wpx: 60 },  // Job
            { wpx: 140 }, // Tab
            { wpx: 200 }, // Row ID
            { wpx: 140 }, // Field
            { wpx: 160 }, // Prev Value
            { wpx: 160 }, // New Value
            { wpx: 50 },  // Env
            { wpx: 90 },  // Source
            { wpx: 280 }, // Version GUID
            { wpx: 280 }, // FUD GUID
            { wpx: 90 },  // Audit ID
            { wpx: 360 }, // Prev Row JSON
            { wpx: 360 }, // New Row JSON
        ];
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'Change History');
        const stamp = moment().format('YYYYMMDD-HHmmss');
        xlsx.writeFile(wb, `change-history-${stamp}.xlsx`);
    };

    return (
        <View style={{ padding: 20, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 16 }}>
                <Text style={{ fontSize: 20, fontWeight: '700', color: '#0f172a' }}>Change History</Text>
                <Text style={{ fontSize: 12, color: '#64748b' }}>
                    {rows.length} row{rows.length === 1 ? '' : 's'} — {stats.INSERT} ins / {stats.UPDATE} upd / {stats.DELETE} del
                </Text>
            </View>

            {/* Filter bar */}
            <View style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    {[
                        { label: 'Job (BU_ID)', value: filterJob, set: setFilterJob, w: 130, placeholder: 'all' },
                        { label: 'Version GUID', value: filterVersion, set: setFilterVersion, w: 220, placeholder: 'all' },
                        { label: 'User Email', value: filterEmail, set: setFilterEmail, w: 220, placeholder: 'someone@strikeusa.com' },
                        { label: 'Row ID', value: filterRowId, set: setFilterRowId, w: 220, placeholder: 'exact match' },
                        { label: 'Start (YYYY-MM-DD)', value: filterStart, set: setFilterStart, w: 150, placeholder: '' },
                        { label: 'End (YYYY-MM-DD)', value: filterEnd, set: setFilterEnd, w: 150, placeholder: '' },
                    ].map((f) => (
                        <View key={f.label}>
                            <Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>{f.label}</Text>
                            <TextInput
                                value={f.value}
                                onChangeText={f.set}
                                placeholder={f.placeholder}
                                style={{ width: f.w, height: 26, fontSize: 12, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                            />
                        </View>
                    ))}
                    <View>
                        <Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>Tab</Text>
                        <select value={filterTab} onChange={e => setFilterTab(e.target.value)} style={{ width: 200, height: 26, fontSize: 12, padding: '0 4px', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                            {TAB_OPTIONS.map(t => <option key={t} value={t}>{t || '(all tabs)'}</option>)}
                        </select>
                    </View>
                    <View>
                        <Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>Action</Text>
                        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ width: 110, height: 26, fontSize: 12, padding: '0 4px', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                            {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a || '(all)'}</option>)}
                        </select>
                    </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <Pressable
                        onPress={() => fetchPage(false)}
                        style={{ backgroundColor: '#1e40af', padding: '6px 14px', borderRadius: 4, cursor: loading ? 'wait' : 'pointer' }}
                    >
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>{loading ? 'Loading…' : 'Refresh'}</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => { setFilterJob(''); setFilterVersion(''); setFilterTab(''); setFilterEmail(''); setFilterAction(''); setFilterRowId(''); setFilterStart(''); setFilterEnd(''); }}
                        style={{ backgroundColor: '#e2e8f0', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' }}
                    >
                        <Text style={{ color: '#334155', fontSize: 12, fontWeight: '600' }}>Clear filters</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => { setFilterJob(selectedJob || ''); setFilterVersion(selectedVersion || ''); }}
                        style={{ backgroundColor: '#e2e8f0', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' }}
                    >
                        <Text style={{ color: '#334155', fontSize: 12, fontWeight: '600' }}>Scope to current job/version</Text>
                    </Pressable>
                    <Pressable
                        onPress={exportExcel}
                        disabled={rows.length === 0}
                        style={{ backgroundColor: rows.length === 0 ? '#a7f3d0' : '#059669', padding: '6px 14px', borderRadius: 4, cursor: rows.length === 0 ? 'not-allowed' : 'pointer' }}
                    >
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Export to Excel</Text>
                    </Pressable>
                    {error ? <Text style={{ color: '#dc2626', fontSize: 12 }}>Error: {error}</Text> : null}
                </View>
            </View>

            {/* Results table */}
            <View style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                {/* Header row */}
                <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', padding: '6px 8px', alignItems: 'center' }}>
                    {[
                        { label: 'When', w: 160 },
                        { label: 'Action', w: 70 },
                        { label: 'User', w: 200 },
                        { label: 'Job', w: 60 },
                        { label: 'Tab', w: 140 },
                        { label: 'Row', w: 150 },
                        { label: 'Field', w: 130 },
                        { label: 'Prev', w: 140 },
                        { label: 'New', w: 140 },
                        { label: 'Env', w: 60 },
                    ].map(c => (
                        <Text key={c.label} style={{ width: c.w, fontSize: 10, color: '#f1f5f9', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, paddingRight: 6 }}>{c.label}</Text>
                    ))}
                </View>
                {/* Empty state */}
                {!loading && rows.length === 0 ? (
                    <View style={{ padding: 24 }}>
                        <Text style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No audit rows match these filters.</Text>
                    </View>
                ) : null}
                {/* Data rows */}
                {rows.map((r, idx) => {
                    const isOpen = expandedId === r.Audit_ID;
                    return (
                        <React.Fragment key={r.Audit_ID}>
                            <Pressable
                                onPress={() => setExpandedId(isOpen ? null : r.Audit_ID)}
                                style={{
                                    flexDirection: 'row', padding: '4px 8px', alignItems: 'center',
                                    backgroundColor: isOpen ? '#eff6ff' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                                    borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                                }}
                            >
                                <Text style={{ width: 160, fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>{fmtDate(r.Audit_Datetime)}</Text>
                                <Text style={{ width: 70, fontSize: 11, fontWeight: '700', color: actionColor(r.Action_Type) }}>{r.Action_Type}</Text>
                                <Text style={{ width: 200, fontSize: 11, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.User_Email || r.User_Name || r.User_ID || '—'}</Text>
                                <Text style={{ width: 60, fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>{r.BU_ID || ''}</Text>
                                <Text style={{ width: 140, fontSize: 11, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.Forecast_Tab || ''}</Text>
                                <Text style={{ width: 150, fontSize: 10, color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncate(r.Forecast_Row_ID, 22)}</Text>
                                <Text style={{ width: 130, fontSize: 11, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.Forecast_Field_Name || ''}</Text>
                                <Text style={{ width: 140, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncate(r.Prev_Value, 18)}</Text>
                                <Text style={{ width: 140, fontSize: 11, color: '#1e293b', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncate(r.New_Value, 18)}</Text>
                                <Text style={{ width: 60, fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{r.Env || ''}</Text>
                            </Pressable>
                            {isOpen ? (
                                <View style={{ padding: 12, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <View style={{ flexDirection: 'row', gap: 12 }}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ fontSize: 10, fontWeight: '700', color: '#dc2626', marginBottom: 4, textTransform: 'uppercase' }}>Prev row</Text>
                                            <pre style={{ margin: 0, padding: 8, backgroundColor: '#ffffff', border: '1px solid #fecaca', borderRadius: 4, fontSize: 10, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280, overflow: 'auto' }}>{r.Prev_Row_JSON ? formatJson(r.Prev_Row_JSON) : '(none)'}</pre>
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ fontSize: 10, fontWeight: '700', color: '#059669', marginBottom: 4, textTransform: 'uppercase' }}>New row</Text>
                                            <pre style={{ margin: 0, padding: 8, backgroundColor: '#ffffff', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 10, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280, overflow: 'auto' }}>{r.New_Row_JSON ? formatJson(r.New_Row_JSON) : '(none)'}</pre>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 16, marginTop: 8 }}>
                                        <Text style={{ fontSize: 10, color: '#64748b' }}>Audit_ID: <Text style={{ fontFamily: 'monospace', color: '#1e293b' }}>{r.Audit_ID}</Text></Text>
                                        <Text style={{ fontSize: 10, color: '#64748b' }}>Source: <Text style={{ fontFamily: 'monospace', color: '#1e293b' }}>{r.Source || ''}</Text></Text>
                                        <Text style={{ fontSize: 10, color: '#64748b' }}>FUD GUID: <Text style={{ fontFamily: 'monospace', color: '#1e293b' }}>{r.Forecast_User_Data_GUID || '—'}</Text></Text>
                                        <Text style={{ fontSize: 10, color: '#64748b' }}>Version: <Text style={{ fontFamily: 'monospace', color: '#1e293b' }}>{r.Version_GUID || '—'}</Text></Text>
                                    </View>
                                </View>
                            ) : null}
                        </React.Fragment>
                    );
                })}
                {/* Load more footer */}
                {hasMore ? (
                    <View style={{ padding: 10, alignItems: 'center', backgroundColor: '#f8fafc' }}>
                        <Pressable
                            onPress={() => fetchPage(true)}
                            style={{ backgroundColor: '#e2e8f0', padding: '6px 14px', borderRadius: 4, cursor: loading ? 'wait' : 'pointer' }}
                        >
                            <Text style={{ color: '#334155', fontSize: 12, fontWeight: '600' }}>{loading ? 'Loading…' : `Load older (${PAGE_LIMIT} more)`}</Text>
                        </Pressable>
                    </View>
                ) : null}
            </View>
        </View>
    );
};

// Pretty-print JSON string. Falls back to raw text on parse error.
const formatJson = (s) => {
    try {
        return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
        return s;
    }
};

export default AuditLog;
